<template>
  <div data-focus-view>
    <nav class="tw:!flex tw:!items-center tw:!text-sm tw:!text-gray-500 tw:!mb-6 tw:!flex-wrap tw:!gap-2">
      <a href="#" @click.prevent="$emit('back-home')" class="tw:!text-gray-500 tw:!no-underline tw:!cursor-pointer tw:!transition-colors od-nav-hover">
        {{ $t('nav.esimShop') }}
      </a>
      <ChevronRight class="tw:!w-4 tw:!h-4 tw:!text-gray-400" />
      <a href="#" @click.prevent="$emit('back-packages')" class="tw:!text-gray-500 tw:!no-underline tw:!cursor-pointer tw:!transition-colors od-nav-hover">
        {{ store.countryName }}
      </a>
      <ChevronRight class="tw:!w-4 tw:!h-4 tw:!text-gray-400" />
      <a href="#" @click.prevent="$emit('back')" class="tw:!text-gray-500 tw:!no-underline tw:!cursor-pointer tw:!transition-colors od-nav-hover">
        {{ $t('register.breadcrumb') }}
      </a>
      <ChevronRight class="tw:!w-4 tw:!h-4 tw:!text-gray-400" />
      <span class="tw:!font-semibold od-nav-active">{{ $t('checkout.title') }}</span>
    </nav>

    <div class="checkout-layout">
      <div class="tw:!bg-white tw:!rounded-3xl tw:!p-6 tw:!shadow-sm tw:!order-2 md:tw:!order-1">
        <h3 class="tw:!text-xl tw:!font-bold tw:!text-gray-900 tw:!m-0 tw:!mb-6">{{ $t('checkout.section.package') }}</h3>

        <div class="tw:!flex tw:!items-center tw:!gap-4 tw:!pb-6 tw:!border-b tw:!border-gray-200 tw:!mb-6">
          <div class="tw:!w-14 tw:!h-14 tw:!rounded-full tw:!overflow-hidden tw:!border-2 tw:!border-gray-50 tw:!shrink-0">
            <img
              :src="`https://flagcdn.com/${store.countrySlug.toLowerCase()}.svg`"
              :alt="store.countryName"
              class="tw:!w-full tw:!h-full tw:!object-cover"
            />
          </div>
          <div class="tw:!flex tw:!flex-col tw:!gap-1">
            <h2 class="tw:!text-xl tw:!font-bold tw:!text-gray-900 tw:!m-0">{{ store.countryName }}</h2>
            <div class="tw:!flex tw:!gap-4 tw:!mt-1">
              <div class="tw:!flex tw:!items-center tw:!gap-2 tw:!text-sm tw:!text-gray-500">
                <Wifi class="tw:!h-4 tw:!w-4" /> {{ store.selectedPackage?.speed }}
              </div>
              <div class="tw:!flex tw:!items-center tw:!gap-2 tw:!text-sm tw:!text-gray-500">
                <ClockArrowUp class="tw:!h-4 tw:!w-4" /> {{ store.selectedPackage?.unusedValidTime }} {{ $t('checkout.field.days') }}
              </div>
            </div>
          </div>
        </div>

        <div class="tw:!flex tw:!flex-col tw:!gap-3 tw:!mb-6">
          <div class="tw:!flex tw:!items-center tw:!gap-3">
            <MapPin class="tw:!w-5 tw:!h-5 tw:!text-gray-400 tw:!shrink-0" />
            <span class="tw:!text-sm tw:!text-gray-500">{{ $t('checkout.field.coverage') }}:</span>
            <span class="tw:!text-sm tw:!font-semibold tw:!text-gray-900">{{ store.countryName }}</span>
          </div>
          <div class="tw:!flex tw:!items-center tw:!gap-3">
            <ArrowDownUp class="tw:!w-5 tw:!h-5 tw:!text-gray-400 tw:!shrink-0" />
            <span class="tw:!text-sm tw:!text-gray-500">{{ $t('checkout.field.traffic') }}:</span>
            <span class="tw:!text-sm tw:!font-semibold tw:!text-gray-900">{{ formatData(store.selectedPackage?.volume || 0) }}</span>
          </div>
          <div class="tw:!flex tw:!items-center tw:!gap-3">
            <CalendarCheck class="tw:!w-5 tw:!h-5 tw:!text-gray-400 tw:!shrink-0" />
            <span class="tw:!text-sm tw:!text-gray-500">{{ $t('checkout.field.validity') }}:</span>
            <span class="tw:!text-sm tw:!font-semibold tw:!text-gray-900">{{ store.selectedPackage?.description }}</span>
          </div>
        </div>

        <div class="tw:!flex tw:!justify-between tw:!items-center tw:!pt-4 tw:!border-t tw:!border-gray-200">
          <span class="tw:!text-lg tw:!font-bold tw:!text-gray-900">{{ $t('checkout.field.total') }}:</span>
          <span class="tw:!text-2xl tw:!font-bold tw:!text-gray-900">${{ formatPrice(store.totalAmount) }}</span>
        </div>

        <div class="tw:!flex tw:!flex-col tw:!gap-2 tw:!mt-4">
          <div class="tw:!flex tw:!items-center tw:!gap-3 tw:!bg-green-50 tw:!rounded-xl tw:!px-4 tw:!py-3">
            <Check class="tw:!w-4 tw:!h-4 tw:!text-green-500 tw:!shrink-0" />
            <span class="tw:!text-sm tw:!text-green-800">{{ $t('checkout.trust.instantDelivery') }}</span>
          </div>
          <div class="tw:!flex tw:!items-center tw:!gap-3 tw:!bg-green-50 tw:!rounded-xl tw:!px-4 tw:!py-3">
            <RefreshCw class="tw:!w-4 tw:!h-4 tw:!text-green-500 tw:!shrink-0" />
            <span class="tw:!text-sm tw:!text-green-800">{{ $t('checkout.trust.moneyBack') }}</span>
          </div>
          <div class="tw:!flex tw:!items-center tw:!gap-3 tw:!bg-green-50 tw:!rounded-xl tw:!px-4 tw:!py-3">
            <MailPlus class="tw:!w-4 tw:!h-4 tw:!text-green-500 tw:!shrink-0" />
            <span class="tw:!text-sm tw:!text-green-800">{{ $t('checkout.trust.support') }}</span>
          </div>
        </div>

        <img :src="visaTag" alt="Payment" class="tw:!w-xs tw:!mx-auto tw:!mt-4 tw:!object-contain" />
      </div>

      <div class="tw:!bg-white tw:!rounded-3xl tw:!p-6 tw:!shadow-sm tw:!order-1 md:tw:!order-2">
        <h3 class="tw:!text-xl tw:!font-bold tw:!text-gray-900 tw:!m-0 tw:!mb-6">{{ $t('checkout.payment.title') }}</h3>

        <!-- <StripCardPayment
          v-if="clientSecret"
          :client-secret="clientSecret"
          :amount="store.totalAmount"
          :email="store.registerEmail"
          @success="onStripeSuccess"
        />

        <StripeExpressPayment
          v-if="clientSecret"
          :client-secret="clientSecret"
          :amount="store.totalAmount"
          :email="store.registerEmail"
          @success="onStripeSuccess"
          @unavailable="expressUnavailable = true"
        /> -->

        <div class="section-divider tw:!my-6">
          <span class="tw:!px-4 tw:!text-sm tw:!font-semibold tw:!text-gray-500">{{ $t('checkout.payment.orPayWith') }}</span>
        </div>

        <div :ref="(el) => { element.paypal.value = el as HTMLElement }"></div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import visaTag from '~/assets/images/visa-tag.webp'
import { onMounted, ref } from 'vue'
// import type { PaymentIntent } from '@stripe/stripe-js'
import { formatData, formatPrice } from '~/utility/helper'
import { ChevronRight, MapPin, CalendarCheck, ArrowDownUp, Wifi, ClockArrowUp, RefreshCw, Check, MailPlus } from 'lucide-vue-next'
import { usePaypal } from '~/composables/usePayment'
import { axios } from '~/composables/general'
import { useStore } from '~/composables/widgetMode'
// import StripCardPayment from '~/components/payment-options/StripCardPayment.vue'
// import StripeExpressPayment from '~/components/payment-options/StripeExpressPayment.vue'
const store = useStore()

const emit = defineEmits<{
  'back': []
  'back-packages': []
  'success': [orderNo: string]
  'back-home': []
}>()

// const clientSecret = ref('')
// const paymentIntentId = ref('')
// const expressUnavailable = ref(false)

const element = {
  paypal: ref<HTMLElement | null>(null),
}

// const onStripeSuccess = async (paymentIntent: PaymentIntent) => {
//   const { data } = await axios.post<{ orderNo: string }>(
//     `/charge/payments/invoice/${store.currentInvoice}/stripe/capture`,
//     { payment_intent_id: paymentIntent.id }
//   )
//   if (data.orderNo) {
//     emit('success', data.orderNo)
//   }
// }

onMounted(async () => {
  const paypal = await usePaypal()

  paypal.Buttons!({
    style: {
      shape: 'rect',
      layout: 'vertical',
      color: 'gold',
      label: 'paypal',
    },
    async createOrder() {
      const { data } = await axios.post<{ id: string }>(`/charge/payments/invoice/${store.currentInvoice}/paypal`)
      return data.id
    },
    async onApprove({ orderID }) {
      const { data } = await axios.post<{ orderNo: string }>(`/charge/payments/invoice/${store.currentInvoice}/paypal/capture`, {
        order_id: orderID
      })
      if (data.orderNo) {
        emit('success', data.orderNo)
      }
    },
  }).render(element.paypal.value!)
})

// onMounted(async () => {
//   const { data } = await axios.post<{ clientSecret: string; payment_intent_id: string }>(
//     `/charge/payments/invoice/${store.currentInvoice}/stripe`
//   )
//   clientSecret.value = data.clientSecret
//   paymentIntentId.value = data.payment_intent_id
// })
</script>

<style scoped>
.checkout-layout {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  align-items: start;
}

@media (max-width: 768px) {
  .checkout-layout {
    grid-template-columns: 1fr;
  }
}

.section-divider {
  display: flex;
  align-items: center;
  margin: 1.5rem 0;
}

.section-divider::before,
.section-divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: #e5e7eb;
}

.od-nav-active {
  color: var(--esim-primary, #facc15);
}
</style>